import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { DialogRef } from '@ngneat/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { APP_SPINNER_NAME } from '#common/constants/top-front';
import { BuilderLeftEnum } from '#common/enums/builder-left.enum';
import { RepoTypeEnum } from '#common/enums/repo-type.enum';
import { ResponseInfoStatusEnum } from '#common/enums/response-info-status.enum';
import { ToBackendRequestInfoNameEnum } from '#common/enums/to/to-backend-request-info-name.enum';
import { isDefined } from '#common/functions/is-defined';
import type {
  ToBackendCommitRepoRequestPayload,
  ToBackendCommitRepoResponse
} from '#common/zod/to-backend/repos/to-backend-commit-repo';
import { RepoQuery } from '#front/app/queries/repo.query';
import { SessionQuery } from '#front/app/queries/session.query';
import { SessionsQuery } from '#front/app/queries/sessions.query';
import { ApiService } from '#front/app/services/api.service';
import { FileService } from '#front/app/services/file.service';
import { SharedModule } from '../../shared/shared.module';

export interface CommitDialogDialogData {
  apiService: ApiService;
  projectId: string;
  repoId: string;
  repoType: RepoTypeEnum;
  branchId: string;
  builderLeft: BuilderLeftEnum;
  fileId: string;
}

@Component({
  selector: 'm-commit-dialog',
  templateUrl: './commit-dialog.component.html',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, ReactiveFormsModule, SharedModule]
})
export class CommitDialogComponent implements OnInit {
  @HostListener('window:keyup.esc')
  onEscKeyUp() {
    this.ref.close();
  }

  @ViewChild('commitMessage') commitMessageElement: ElementRef;

  commitForm: FormGroup;

  isSessionRepo = false;

  constructor(
    public ref: DialogRef<CommitDialogDialogData>,
    private fb: FormBuilder,
    private spinner: NgxSpinnerService,
    private fileService: FileService,
    private repoQuery: RepoQuery,
    private sessionQuery: SessionQuery,
    private sessionsQuery: SessionsQuery
  ) {}

  ngOnInit() {
    this.isSessionRepo = this.ref.data.repoType === RepoTypeEnum.Session;

    let epochTs = Math.floor(new Date().getTime() / 1000);

    this.commitForm = this.fb.group({
      message: [`c${epochTs}`, [Validators.required, Validators.maxLength(255)]]
    });

    setTimeout(() => {
      this.commitMessageElement.nativeElement.focus();
    }, 0);
  }

  commit() {
    this.commitForm.markAllAsTouched();

    if (!this.commitForm.valid) {
      return;
    }

    this.ref.close();

    let apiService: ApiService = this.ref.data.apiService;

    let payload: ToBackendCommitRepoRequestPayload = {
      projectId: this.ref.data.projectId,
      repoId: this.ref.data.repoId,
      branchId: this.ref.data.branchId,
      commitMessage: this.commitForm.value.message
    };

    this.spinner.show(APP_SPINNER_NAME);

    apiService
      .req({
        pathInfoName: ToBackendRequestInfoNameEnum.ToBackendCommitRepo,
        payload: payload
      })
      .pipe(
        map((resp: ToBackendCommitRepoResponse) => {
          if (resp.info?.status === ResponseInfoStatusEnum.Ok) {
            this.repoQuery.update(resp.payload.repo);

            let respSession = resp.payload.session;

            if (isDefined(respSession)) {
              this.sessionQuery.updatePart({
                status: respSession.status,
                archiveReason: respSession.archiveReason
              });

              let sessions = this.sessionsQuery.getValue().sessions;

              this.sessionsQuery.updatePart({
                sessions: sessions.map(s =>
                  s.sessionId === respSession.sessionId
                    ? {
                        ...s,
                        status: respSession.status,
                        archiveReason: respSession.archiveReason
                      }
                    : s
                )
              });
            }

            return true;
          } else {
            return false;
          }
        }),
        switchMap(x =>
          x === true && isDefined(this.ref.data.fileId)
            ? this.fileService.getFile({
                fileId: this.ref.data.fileId,
                builderLeft: this.ref.data.builderLeft
              })
            : of([])
        ),
        tap(x => {
          this.spinner.hide(APP_SPINNER_NAME);
          this.fileService.refreshSecondFile();
        }),
        take(1)
      )
      .subscribe();
  }

  cancel() {
    this.ref.close();
  }
}
